import React, { useMemo, useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Card, Button, Badge, Label, Input, Switch} from '@/components/ui/';
import { ToggleSection } from '@/components/form/ToggleSection';
import { AnimatePresence, motion } from 'framer-motion';
import { EASE_EXPO_OUT, DURATION_MEDIUM } from '@/lib/motion';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { NumberInputRow } from '@/components/form/NumberInputRow';
import { AnimatedFormError } from '@/components/form/AnimatedFormError';
import type { OvernightConfig, RateMultiplier, Role } from '@/types';
import type { ExtendedTier } from '@/types/ExtendedTier';
import { ApplicableRolesRow } from '../shared/AdvancedSettingsSection';
import { hasAnyHalfDayRoles } from '../shared/utils';
import { calculateOvernightMarkdownVariables } from './utils/markdownVariables';
import { CustomizedFieldIndicator } from '../../form/CustomizedFieldIndicator';
import { OvernightPremiumMethodSelector } from './components/OvernightPremiumMethodSelector';
import { fieldLabelClass, fieldDescriptionClass } from '@/components/config_forms/shared/surcharge/fieldStyles';
import { ArrowRight, Settings, DollarSign } from 'lucide-react';
import { ConfigSection } from '../shared/ConfigSection';
import { SurchargeMethodPanel } from '../shared/surcharge/SurchargeMethodPanel';
import { useMethodSwapAnimation } from '../shared/surcharge/useMethodSwapAnimation';
import { HourlyPremiumMethodSection } from './sections/HourlyPremiumMethodSection';
import { FlatRateSection } from './sections/FlatRateSection';
import { CreditExtraTimeSection } from './sections/CreditExtraTimeSection';
import { MultiplierOffsetSection } from './sections/MultiplierOffsetSection';
import { useOvertimeLadderInfo } from '@/hooks/useOvertimeLadderInfo';
import { OffsetConfigSection, type OffsetConfig } from '../shared/OffsetConfigSection';

interface OvernightConfigFormProps {
  title: string;
  description: string;
  config: OvernightConfig;
  onConfigChange: (updates: Partial<OvernightConfig>) => void;
  context: 'user' | 'client';
  isCustomized?: boolean;
  onRevert?: () => void;
  noCard?: boolean;
  startTimeProps?: Record<string, unknown>;
  startTimeError?: React.ReactNode;
  endTimeProps?: Record<string, unknown>;
  endTimeError?: React.ReactNode;
  premiumValueProps?: Record<string, unknown>;
  premiumValueError?: React.ReactNode;
  flatRateAmountProps?: Record<string, unknown>;
  flatRateAmountError?: React.ReactNode;
  timeCreditMinutesProps?: Record<string, unknown>;
  timeCreditMinutesError?: React.ReactNode;
  hasThresholdMultipliers?: boolean;
  rateMultipliers?: RateMultiplier[];
  roles?: Role[];
  minimumBillableHours?: number | null;
  hideToggle?: boolean;
  thresholdEnabled?: boolean;
  onThresholdEnabledChange?: (checked: boolean) => void;
  thresholdType?: 'percentage' | 'hours' | null;
  onThresholdTypeChange?: (type: 'percentage' | 'hours') => void;
  percentageThresholdProps?: Record<string, unknown>;
  percentageThresholdError?: React.ReactNode;
  hoursThresholdProps?: Record<string, unknown>;
  hoursThresholdError?: React.ReactNode;
  customizedFields?: Record<string, boolean>;
  extendedOvertimeEnabled?: boolean;
  onExtendedOvertimeEnabledChange?: (enabled: boolean) => void;
  extendedOvertimeTiers?: ExtendedTier[];
  onExtendedOvertimeTiersChange?: (tiers: ExtendedTier[]) => void;
  defaultExtendedTiers?: ExtendedTier[];
}


const OvernightConfigFormComponent: React.FC<OvernightConfigFormProps> = ({
  title,
  description,
  config,
  onConfigChange,
  context,
  isCustomized,
  onRevert,
  noCard,
  startTimeProps,
  startTimeError,
  endTimeProps,
  endTimeError,
  premiumValueProps,
  premiumValueError,
  flatRateAmountProps,
  flatRateAmountError,
  timeCreditMinutesProps,
  timeCreditMinutesError,
  hasThresholdMultipliers = true,
  rateMultipliers = [],
  roles = [],
  minimumBillableHours,
  hideToggle,
  thresholdEnabled,
  onThresholdEnabledChange,
  thresholdType,
  onThresholdTypeChange,
  percentageThresholdProps,
  percentageThresholdError,
  hoursThresholdProps,
  hoursThresholdError,
  customizedFields = {},
  extendedOvertimeEnabled,
  onExtendedOvertimeEnabledChange,
  extendedOvertimeTiers,
  onExtendedOvertimeTiersChange,
  defaultExtendedTiers,
}) => {
  const { applyOvernightPremium, overnightMethod } = config;
  const [triggerConditionsOpen, setTriggerConditionsOpen] = useState(true);
  const [premiumConfigOpen, setPremiumConfigOpen] = useState(true);

  const {
    containerStyle: methodContainerStyle,
    containerRef: methodContentContainerRef,
    activePanelRef: activeMethodPanelRef,
    fadeDuration: methodFadeDuration,
  } = useMethodSwapAnimation(overnightMethod);

  // Dynamic description for percentage threshold
  const exampleShiftHours = useMemo(() => minimumBillableHours || 10, [minimumBillableHours]);

  const sortedOvertimeMultipliers = useMemo(() => {
    const sampleRole = roles.find(r => r.billingStructure === 'day_rate') || roles[0];
    return rateMultipliers
      .filter(m => m.code !== 'STRAIGHT_TIME')
      .sort((a, b) => (a.defaultMultiplier || 1) - (b.defaultMultiplier || 1));
  }, [rateMultipliers, roles]);

  const { highestOvertimeRate, highestOvertimeTierName, maxExtendedTiers } = useOvertimeLadderInfo(rateMultipliers);

  // OffsetConfigSection wiring. See mirrored logic in MealBreakConfigForm.
  // Overnight does not support 'previous_shift' (no prior-shift concept).
  const offsetConfig: OffsetConfig = useMemo(() => ({
    floor_source: config.floorSource ?? 'none',
    overtime_offset: config.overtimeOffset ?? 0,
    floor_tier_code: config.floorTierCode ?? null,
    floor_tier_custom_value: config.floorTierCustomValue ?? null,
  }), [
    config.floorSource, config.overtimeOffset, config.floorTierCode,
    config.floorTierCustomValue,
  ]);
  const handleOffsetChange = (next: OffsetConfig) => {
    onConfigChange({
      floorSource: next.floor_source === 'previous_shift' ? 'none' : next.floor_source,
      overtimeOffset: next.overtime_offset,
      floorTierCode: next.floor_tier_code,
      floorTierCustomValue: next.floor_tier_custom_value,
    });
  };

  const firstOvertimeMultiplierName = useMemo(
    () => sortedOvertimeMultipliers[0]?.name || 'Overtime',
    [sortedOvertimeMultipliers]
  );

  const getMarkdownVariables = useCallback((method: string) => {
    return calculateOvernightMarkdownVariables(
      roles,
      rateMultipliers,
      config.timeCreditMinutes ?? 30,
      minimumBillableHours ?? 10,
      config.premiumType === 'percentage' ? (config.premiumValue ?? 10) : 10,
      config.premiumType !== 'percentage' ? (config.premiumValue ?? 5) : 5,
      config.flatRateAmount ?? 50,
      config.startTime ?? '00:00',
      config.endTime ?? '06:00',
      config.premiumBasis === 'ot_inclusive',
      thresholdEnabled,
      thresholdType,
      config.percentageThreshold,
      config.hoursThreshold,
      method as OvernightConfig['overnightMethod'],
      config.premiumType ?? 'fixed'
    );
  }, [roles, rateMultipliers, config, minimumBillableHours, thresholdEnabled, thresholdType]);


  const anyHalfDayRoles = useMemo(() => hasAnyHalfDayRoles(roles, config.applicableRoleIds), [roles, config.applicableRoleIds]);

  const formFields = (
    <div className="space-y-3">
      <ConfigSection
        title="Trigger Conditions"
        description="Configure when overnight premium applies."
        icon={Settings}

        isOpen={triggerConditionsOpen}
        onOpenChange={setTriggerConditionsOpen}
      >
        <div className="space-y-5">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Label className={fieldLabelClass}>Overnight Period</Label>
                          <CustomizedFieldIndicator isCustomized={customizedFields?.overnight_start_time || customizedFields?.overnight_end_time} context={context} />
                        </div>
                        <p className={cn(fieldDescriptionClass, "mt-1")}>
                          Define the time window for overnight premium calculation.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-4 w-full md:max-w-[95%] md:ml-auto items-start">
                          <div className="space-y-1">
                            <Input
                              id="overnight-start-time"
                              aria-label="Overnight period start time"
                              type="time"
                              variant="underline"
                              radius="none"
                              {...(startTimeProps || {})}
                              className={cn("dark:[color-scheme:dark] justify-center font-mono", (startTimeProps as Record<string, unknown> | undefined)?.className as string | undefined)}
                            />
                            <AnimatedFormError>{startTimeError}</AnimatedFormError>
                          </div>
                          <div className="hidden sm:flex items-center justify-center pt-2">
                            <ArrowRight className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="space-y-1">
                            <Input
                              id="overnight-end-time"
                              aria-label="Overnight period end time"
                              type="time"
                              variant="underline"
                              radius="none"
                              {...(endTimeProps || {})}
                              className={cn("dark:[color-scheme:dark] justify-center font-mono", (endTimeProps as Record<string, unknown> | undefined)?.className as string | undefined)}
                            />
                            <AnimatedFormError>{endTimeError}</AnimatedFormError>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-center">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Label className={fieldLabelClass}>Minimum Threshold Trigger</Label>
                          <CustomizedFieldIndicator isCustomized={customizedFields?.threshold_enabled} context={context} />
                        </div>
                        <p className={fieldDescriptionClass}>Apply premium only if a minimum amount of time is worked during the overnight period.</p>
                      </div>
                      <div className="flex justify-start lg:justify-end">
                        <Switch
                          id="overnight-threshold-enabled"
                          aria-label="Enable minimum threshold trigger"
                          checked={thresholdEnabled ?? false}
                          onCheckedChange={onThresholdEnabledChange}
                        />
                      </div>
                    </div>
                    <AnimatedCollapse open={thresholdEnabled ?? false} className="space-y-4 pl-4 border-l-2 border-border/50">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-center">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Label className={fieldLabelClass}>
                                  Threshold Type
                                </Label>
                                <CustomizedFieldIndicator isCustomized={customizedFields?.threshold_type} context={context} />
                              </div>
                              <p className={fieldDescriptionClass}>Choose whether the threshold is based on hours or percentage of the shift.</p>
                            </div>
                            <div className="flex justify-start lg:justify-end">
                              <div className="inline-flex items-center gap-2 bg-muted/20 px-2 py-1 rounded border">
                                <span className={cn("text-xs font-medium transition-colors", thresholdType !== 'percentage' && '!text-foreground', thresholdType === 'percentage' && '!text-muted-foreground')}>Hours</span>
                                <Switch
                                  aria-label="Toggle between hours and percentage threshold"
                                  checked={thresholdType === 'percentage'}
                                  onCheckedChange={(checked) => onThresholdTypeChange?.(checked ? 'percentage' : 'hours')}
                                  className="h-4 w-8 [&>span]:h-3 [&>span]:w-3 [&>span[data-state=checked]]:translate-x-4"
                                />
                                <span className={cn("text-xs font-medium transition-colors", thresholdType === 'percentage' && '!text-foreground', thresholdType !== 'percentage' && '!text-muted-foreground')}>Percentage</span>
                              </div>
                            </div>
                          </div>

                          <AnimatePresence mode="wait">
                            {thresholdType === 'percentage' ? (
                              <motion.div key="percentage-input" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT }}>
                                <NumberInputRow
                                  id="percentage-threshold"
                                  label="Minimum Percentage"
                                  description={`Premium applies if at least this % of shift is overnight.`}
                                  unit="%"
                                  inputProps={percentageThresholdProps ?? {}}
                                  errorElement={<AnimatedFormError>{percentageThresholdError}</AnimatedFormError>}
                                />
                              </motion.div>
                            ) : (
                              <motion.div key="hours-input" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT }}>
                                <NumberInputRow
                                  id="hours-threshold"
                                  label="Minimum Hours"
                                  description={`Premium applies if at least this many hours are worked within the overnight period.`}
                                  unit="hrs"
                                  inputProps={hoursThresholdProps ?? {}}
                                  errorElement={<AnimatedFormError>{hoursThresholdError}</AnimatedFormError>}
                                />
                              </motion.div>
                            )}
                          </AnimatePresence>
                    </AnimatedCollapse>

                    <ApplicableRolesRow
                      roles={roles}
                      applicableRoleIds={config.applicableRoleIds ?? null}
                      onApplicableRoleIdsChange={(roleIds) => onConfigChange({ applicableRoleIds: roleIds })}
                      isCustomized={customizedFields?.overnight_applicable_role_ids}
                      context={context}
                      featureName="overnight-premium"
                      showHalfDayOption={anyHalfDayRoles}
                      applyOnHalfDays={config.applyOnHalfDays}
                      onApplyOnHalfDaysChange={(checked) => onConfigChange({ applyOnHalfDays: checked })}
                      halfDayDescription="Apply this premium even when the shift is billed as a half day. Ignored for hourly roles."
                      className="mb-4"
                    />
        </div>
      </ConfigSection>

      <ConfigSection
        title="Premium Configuration"
        description="Configure how the overnight premium is calculated."
        icon={DollarSign}

        isOpen={premiumConfigOpen}
        onOpenChange={setPremiumConfigOpen}
      >
        <div>
                    <OvernightPremiumMethodSelector
                      premiumType={overnightMethod}
                      onPremiumTypeChange={(value) => onConfigChange({ overnightMethod: value })}
                      hasThresholdMultipliers={hasThresholdMultipliers}
                      customizedFields={customizedFields}
                      context={context}
                      markdownVariables={getMarkdownVariables}
                    />

                    <div
                      ref={methodContentContainerRef}
                      className="overflow-hidden"
                      style={methodContainerStyle}
                    >
                      <div className="relative grid">
                        <AnimatePresence mode="sync" initial={false}>
                          {overnightMethod === 'hourly_premium' && (
                            <SurchargeMethodPanel
                              key="hourly_premium"
                              activePanelRef={activeMethodPanelRef}
                              transitionDuration={methodFadeDuration}
                            >
                              <HourlyPremiumMethodSection
                                config={config}
                                onConfigChange={onConfigChange}
                                premiumValueProps={premiumValueProps}
                                premiumValueError={premiumValueError}
                                customizedFields={customizedFields}
                                context={context}
                                firstOvertimeMultiplierName={firstOvertimeMultiplierName}
                              />
                            </SurchargeMethodPanel>
                          )}

                          {overnightMethod === 'flat_rate' && (
                            <SurchargeMethodPanel
                              key="flat_rate"
                              activePanelRef={activeMethodPanelRef}
                              transitionDuration={methodFadeDuration}
                            >
                              <FlatRateSection
                                flatRateAmountProps={flatRateAmountProps}
                                flatRateAmountError={flatRateAmountError}
                              />
                            </SurchargeMethodPanel>
                          )}

                          {overnightMethod === 'credit_extra_time' && (
                            <SurchargeMethodPanel
                              key="credit_extra_time"
                              activePanelRef={activeMethodPanelRef}
                              transitionDuration={methodFadeDuration}
                            >
                              <CreditExtraTimeSection
                                timeCreditMinutesProps={timeCreditMinutesProps}
                                timeCreditMinutesError={timeCreditMinutesError}
                              />
                            </SurchargeMethodPanel>
                          )}

                          {overnightMethod === 'offset_overtime' && (
                            <SurchargeMethodPanel
                              key="ladder_offset"
                              activePanelRef={activeMethodPanelRef}
                              transitionDuration={methodFadeDuration}
                            >
                              <MultiplierOffsetSection
                                customizedFields={customizedFields}
                                context={context}
                                firstOvertimeMultiplierName={firstOvertimeMultiplierName}
                                extendedOvertimeEnabled={extendedOvertimeEnabled ?? false}
                                onExtendedOvertimeEnabledChange={onExtendedOvertimeEnabledChange ?? (() => {})}
                                extendedOvertimeTiers={extendedOvertimeTiers ?? []}
                                onExtendedOvertimeTiersChange={onExtendedOvertimeTiersChange ?? (() => {})}
                                highestOvertimeRate={highestOvertimeRate}
                                highestOvertimeTierName={highestOvertimeTierName}
                                defaultTiers={defaultExtendedTiers}
                                maxTiers={maxExtendedTiers}
                              />
                              <div className="mt-5">
                                <OffsetConfigSection
                                  domain="overnight"
                                  method="offset_overtime"
                                  config={offsetConfig}
                                  rateMultipliers={rateMultipliers}
                                  onChange={handleOffsetChange}
                                  extendedOvertimeEnabled={extendedOvertimeEnabled ?? false}
                                  extendedOvertimeTiers={extendedOvertimeTiers ?? []}
                                />
                              </div>
                            </SurchargeMethodPanel>
                          )}

                          {overnightMethod === 'guaranteed_rate' && (
                            <SurchargeMethodPanel
                              key="guaranteed_rate"
                              activePanelRef={activeMethodPanelRef}
                              transitionDuration={methodFadeDuration}
                            >
                              <OffsetConfigSection
                                domain="overnight"
                                method="guaranteed_rate"
                                config={offsetConfig}
                                rateMultipliers={rateMultipliers}
                                onChange={handleOffsetChange}
                                extendedOvertimeEnabled={extendedOvertimeEnabled ?? false}
                                extendedOvertimeTiers={extendedOvertimeTiers ?? []}
                              />
                            </SurchargeMethodPanel>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
        </div>
      </ConfigSection>
    </div>
  );

  if (hideToggle) {
    return formFields;
  }

  const content = (
    <div className="space-y-3 sm:space-y-6">
      {!noCard && (
        <div className="space-y-1 text-center">
          <div className="flex items-center justify-center gap-3">
            <h3 className="font-heading uppercase tracking-wide text-lg text-foreground">{title}</h3>
            {isCustomized && <Badge variant="default">{context === 'client' ? 'Client' : 'Profile'} Override</Badge>}
          </div>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      )}
      <ToggleSection
        id="applyOvernightPremium"
        label="Enable Overnight Premium"
        description="Apply premium compensation for hours worked during a late-night window."
        checked={applyOvernightPremium}
        onCheckedChange={(checked) => onConfigChange({ applyOvernightPremium: checked })}
      >
        {formFields}
      </ToggleSection>
      <AnimatedCollapse open={!!(isCustomized && onRevert)}>
        <div className="pt-4 border-t flex justify-center">
          <Button variant="link" className="p-0 h-auto text-muted-foreground" onClick={onRevert}>
            Revert to Profile Defaults
          </Button>
        </div>
      </AnimatedCollapse>
    </div>
  );

  if (noCard) {
    return content;
  }

  return (
    <Card className="animate-fade-in rounded equipment-panel p-3 pb-4 sm:p-6 max-w-3xl mx-auto">
      {content}
    </Card>
  );
};

export const OvernightConfigForm = React.memo(OvernightConfigFormComponent);
OvernightConfigForm.displayName = 'OvernightConfigForm';
